import * as coreXFormConfigs from '@gig/magic-corex/dist/config';
import {
    formConfigs,
    getPhoneConfig,
    getMaximumAge,
    getMinimumAge,
    getAmendDobHintFeature,
} from '@gig/magic-jurisdictions/dist/configs';
import { translate as $t } from '@gig/magic-common';
import type {
    IFormBuilderElement,
    IFormBuilderStepConfiguration,
    IFormFieldSingleValidation,
} from '@gig/magic-form-builder';
import {
    getInitialFormState,
    IFormBuilderState,
    isNumeric,
    maxLength,
    minLength,
    required,
} from '@gig/magic-form-builder';
import { setReverseMonth } from '@gig/magic-corex';
import { getUserDetails } from '../user-details.helper';
import { config } from '../../../';

export async function getUserDetailsInForm(): Promise<IFormBuilderState> {
    const userDetails = await getUserDetails();

    // only patch the form with already filled in data
    // so that empty fields don't trigger unintended validation errors
    const filledInDetails = userDetails.filter(([, value]) => !!value);

    return getInitialFormState(filledInDetails);
}

export async function userDetailsFormConfig(
    NEXT_BUTTON_ENABLED: boolean,
): Promise<IFormBuilderStepConfiguration[]> {
    const amendDobHint = await getAmendDobHintFeature();
    const minimumAge = await getMinimumAge();
    const maximumAge = await getMaximumAge();

    const date = formConfigs.birthDate({
        amendDobHint,
        minimumAge,
        maximumAge,
    });
    setReverseMonth(date, $t('sweepstakes.user-details.info-date'));

    return [
        {
            name: 'step-1',
            path: 'step-1',
            components: [
                getNameWrapper(),
                date,
                coreXFormConfigs.address(),
                coreXFormConfigs.city(),
                getAddressWrapper(),
                await getPhoneWrapper(),
            ],
            nextButton: {
                text: $t('sweepstakes-user-details-modal.button'),
                alwaysEnabled: NEXT_BUTTON_ENABLED,
            },
            errorMessage: $t('sweepstakes-user-details-modal.error'),
        },
    ];
}

async function getPhoneWrapper() {
    const phoneConfig = await getPhoneConfig();
    const phone = await coreXFormConfigs.phone(phoneConfig);

    phone.properties!['has-info'] = true;
    phone.properties!.info = $t('sign-up-step-two.mobile.hint');

    return phone;
}

function getNameWrapper(): IFormBuilderElement {
    return {
        id: 'name-wrapper',
        component: 'magic-ui-grid',
        isDataField: false,
        properties: {
            cols: 2,
            horizontalGap: 1,
        },
        children: [coreXFormConfigs.firstName(), coreXFormConfigs.lastName()],
    };
}

function getAddressWrapper(): IFormBuilderElement {
    const postCode = coreXFormConfigs.postCode();
    postCode.validation = getPostCodeValidations();

    return {
        id: 'address-wrapper',
        component: 'magic-ui-grid',
        isDataField: false,
        properties: {
            cols: 2,
            horizontalGap: 1,
        },
        children: [
            config.sweepstakes
                ? coreXFormConfigs.state()
                : coreXFormConfigs.country(),
            postCode,
        ],
    };
}

function getPostCodeValidations(): IFormFieldSingleValidation[] {
    return [
        {
            name: 'required',
            fn: required,
            message: $t('sweepstakes.postcode.required-error'),
        },
        {
            name: 'max-length',
            fn: maxLength(5),
            message: $t('sweepstakes.postcode.length-error'),
        },
        {
            name: 'max-length',
            fn: minLength(5),
            message: $t('sweepstakes.postcode.length-error'),
        },
        {
            name: 'containsNumber',
            fn: isNumeric,
            message: $t('sweepstakes.postcode.digits-error'),
        },
    ];
}
